import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGroupInfo } from '../hooks/queries/useGroupInfo';
import { useGroupMembers } from '../hooks/queries/useGroupMembers';
import { useEditExpense } from '../hooks/mutations/useEditExpense';
import { useDeleteExpense } from '../hooks/mutations/useDeleteExpense';
import { useExpenseForm } from '../hooks/useExpenseForm';
import { useExpenseById } from '../hooks/queries/useExpense';
import { useExpenseParticipants } from '../hooks/queries/useExpenseParticipants';

// 컴포넌트 import
import ParticipantList, { SplitType } from '../components/expense/ParticipantList';
import AmountInput from '../components/expense/AmountInput';
import TitleInput from '../components/expense/TitleInput';
import MemberSelect from '../components/expense/MemberSelect';
import { currencies, CurrencyCode } from '../constants/currencies';
import { useParsedCurrencyRates } from '../hooks/useParsedCurrencyRates';
import Header from '../components/Header';
import { getUserData } from '../lib/client';
import { toast } from 'react-hot-toast';
import ExpenseTypeSelect from '../components/expense/ExpenseTypeSelect';

export default function EditExpensePage() {
  const { id, expenseId } = useParams<{ id: string; expenseId: string }>();
  const navigate = useNavigate();
  const currentUser = getUserData();

  // 데이터 로딩
  const { data: group, isLoading: groupLoading, isError: groupError } = useGroupInfo(id);
  const { data: members, isLoading: membersLoading, isError: membersError } = useGroupMembers(id);
  const { data: expense, isLoading: expenseLoading, isError: expenseError } = useExpenseById(expenseId);
  const { data: participants, isLoading: participantsLoading, isError: participantsError } = useExpenseParticipants(expenseId, true);
  const { currencyMap, isLoading: currenciesLoading, isError: currenciesError } = useParsedCurrencyRates();

  // 로딩 및 에러 상태 통합
  const isLoading = groupLoading || membersLoading || currenciesLoading || expenseLoading || participantsLoading;
  const isError = groupError || membersError || currenciesError || expenseError || participantsError;

  // 액션 상태
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const {mutateAsync: editExpense} = useEditExpense();
  const {mutateAsync: deleteExpense} = useDeleteExpense();
  const defaultCurrency = group?.default_currency as CurrencyCode;
  const [isDataInitialized, setIsDataInitialized] = useState(false);
  const isLoadingRef = useRef(false);
  // useExpenseForm 훅 사용
  const {
    // 상태
    expenseType,
    splitType,
    multipliers,
    manualAmounts,
    formattedManualAmounts,
    amount,
    amountForDisplay,
    participants: selectedParticipants,
    paidBy,
    spentAt,
    errors,
    currency,
    exchangeRate,
    exchangeRateInput,
  
    // 함수
    register,
    handleSubmit,
    toggleParticipant,
    adjustMultiplier,
    handleManualAmountInputChange,
    handleManualAmountBlur,
    calculateShare,
    onInputChange,  
    handleSplitTypeChange,
    getParticipantsWithAmount,
    validateManualAmounts,
    handleSpentAtChange,
    onInputBlur,
    onExchangeRateChange,
    onExchangeRateBlur,
    setInitialDataForEdit
  } = useExpenseForm({
    members: members ?? [],
    currentUser,
    defaultCurrency,
    currencyMap,
  });

  // 필수 데이터 확인
  useEffect(() => {
    if (!id || !currentUser) {
      navigate('/', {replace: true});
    }
  }, [id, currentUser, navigate]);

  // 데이터가 로드되면 폼 초기화
  useEffect(() => {
    if (!isDataInitialized && 
        !isLoading && 
        expense && 
        participants && 
        members && 
        defaultCurrency) {
      
      // 참가자 데이터 가공
      const participantsWithAmount = participants.map(p => ({
        id: p.member_id,
        amount: p.amount_in_currency
      }));

      // 초기 데이터 설정
      setInitialDataForEdit({
        title: expense.title,
        amount: expense.amount_in_currency,
        paidBy: expense.paid_by,
        currency: expense.currency,
        exchangeRate: expense.exchange_rate,
        spentAt: expense.spent_at,
        participants: participantsWithAmount as any,
        isTransfer: expense.is_transfer,
        splitType: expense.split_type as SplitType
      });

      setIsDataInitialized(true);
    }
  }, [isLoading, expense, participants, members, defaultCurrency, isDataInitialized]);

  // 삭제 처리
  const handleDelete = async () => {
    if (!expenseId) return;
    
    // 확인 창 표시
    const confirmed = window.confirm('이 지출 기록을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.');
    if (!confirmed) return;
    
    try {
      setDeleteLoading(true);
      await deleteExpense(expenseId);
      
      // 삭제 후 그룹 상세 페이지로 이동
      navigate(`/groups/${id}`, {replace: true});
    } catch (error: any) {
      toast.error(`삭제 오류: ${error.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  // 수정 제출 처리
  const onSubmit = async (data: any) => {
    if (!id || !currentUser || !expenseId) return;
    if (isLoadingRef.current) return;
    
    // 다른 통화 선택 시 환율이 1인 경우 검증
    if (currency !== defaultCurrency && exchangeRate === 1) {
      toast.error('환율 정보를 확인해주세요.');
      return;
    }
    if (exchangeRate <= 0) {
      toast.error('환율을 입력해주세요.');
      return;
    }
    
    try {
      setSubmitLoading(true);
      isLoadingRef.current = true;
      
      if (expenseType === '지출기록') {
        // 수동 입력 방식일 경우 금액 검증
        if (splitType === 'amounts') {
          const validation = validateManualAmounts();
          if (!validation.isValid) {
            toast.error(validation.message);
            setSubmitLoading(false);
            return;
          }
        }
        
        // 참가자별 금액 계산하여 API 요청 형식으로 변환
        const participantsWithAmount = getParticipantsWithAmount();

        // 통화에 맞는 소수점 자릿수로 반올림
        const maxFractionDigits = currencies[defaultCurrency].maximumFractionDigits;
        const amountInDefaultCurrency = Number((data.amount * data.exchangeRate).toFixed(maxFractionDigits));
        const participantsWithAmountInDefaultCurrency = participantsWithAmount.map(participant => ({
          ...participant,
          amount: Number((participant.amount * data.exchangeRate).toFixed(maxFractionDigits)),
          amountInCurrency: Number(participant.amount.toFixed(currencies[currency].maximumFractionDigits))
        }));

        // 지출 수정
        await editExpense({
          expenseId,
          groupId: id,
          title: data.title,
          amount: amountInDefaultCurrency,
          amountInCurrency: amount,
          currency: currency,
          exchangeRate: exchangeRate,
          paidBy: data.paidBy,
          participants: participantsWithAmountInDefaultCurrency,
          isTransfer: false,
          splitType: splitType,
          spentAt: new Date(data.spentAt),
          createdAt: expense?.created_at ? new Date(expense.created_at) : undefined
        });
      } else {
        // 송금기록 처리
        if (!data.receiverId) {
          toast.error('송금 받는 사람을 선택해주세요');
          setSubmitLoading(false);
          return;
        }

        // 통화에 맞는 소수점 자릿수로 반올림
        const amountInDefaultCurrency = Number((data.amount * data.exchangeRate).toFixed(currencies[defaultCurrency].maximumFractionDigits));
        
        // 송금 기록 수정 - 수신자만 참가자로 설정
        const participantsWithAmount = [{
          id: data.receiverId,
          amount: amountInDefaultCurrency,
          amountInCurrency: amount
        }];
        
        // 지출 수정
        await editExpense({
          expenseId,
          groupId: id,
          title: '개인송금',
          amount: amountInDefaultCurrency,
          amountInCurrency: amount,
          currency: currency,
          exchangeRate: exchangeRate,
          paidBy: data.paidBy,
          participants: participantsWithAmount,
          isTransfer: true,
          transferTo: data.receiverId,
          spentAt: new Date(data.spentAt),
          createdAt: expense?.created_at ? new Date(expense.created_at) : undefined
        });
      }
      
      // 성공 후 그룹 상세 페이지로 이동
      navigate(`/groups/${id}`, {replace: true});
    } catch (error: any) {
      toast.error(`오류: ${error.message}`);
    } finally {
      setSubmitLoading(false);
      isLoadingRef.current = false;
    }
  };

  // 에러 UI
  if (isError) {
    return <div className="flex justify-center p-8 text-red-500">데이터를 불러오는 중 오류가 발생했습니다.</div>;
  }

  return (
    <div className="container mx-auto px-4 pt-16 pb-8">
      <Header title={group?.name || '...'} showBackButton onBackClick={() => navigate(-1)} />
      <ExpenseTypeSelect 
        expenseType={expenseType}
        onChange={() => {}}
        disabled={true}
      />

      <div className="max-w-screen-md mx-auto">
        {isLoading || !currency || !defaultCurrency ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-8 h-8 border-4 border-gray-800 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* 금액 */}
            <AmountInput 
              currency={currency}
              formattedAmount={amountForDisplay}
              onInputChange={onInputChange}
              error={errors.amount?.message}
              register={register}
              currencyMap={currencyMap}
              onInputBlur={onInputBlur}
              autoFocus={false}
            />
              
            <input type="hidden" {...register('amount', { 
              required: '금액을 입력해주세요',
              min: { value: 1, message: '금액을 입력해주세요' },
              valueAsNumber: true
            })} />

            {/* 환율 입력 */}
            {currency !== defaultCurrency && (
              <div className="w-full transition-all duration-300 ease-in-out flex items-center -mt-2 ml-1 gap-1">
                <span className='w-[110px] text-gray-500 font-mono'>
                  {currency} 1 = {defaultCurrency}
                </span>
                <input 
                  type="text" 
                  id="exchangeRateInput"
                  className='ml-1 w-[calc(100%-114px)] font-mono-number'
                  value={exchangeRateInput}
                  onChange={onExchangeRateChange}
                  onKeyDown={(e)=>{
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  onBlur={onExchangeRateBlur}
                />
                <input type="hidden" {...register('exchangeRate', {
                  value: exchangeRate,
                  min: { value: 0, message: '환율을 입력해주세요' },
                  valueAsNumber: true
                })} />
              </div>
            )}

            {/* 지출 설명 */}
            <TitleInput 
              register={register}
              disabled={expenseType === '송금기록'}
              error={errors.title?.message}
            />
            
            {/* 결제자/송금자와 날짜 입력 필드를 함께 배치 */}
            <div className="flex gap-4">
              {/* 결제자 또는 송금자 */}
              <div className="flex-1">
                <MemberSelect
                  id="paidBy"
                  label={expenseType === '송금기록' ? '누가' : '결제자'}
                  members={members ?? []}
                  currentUser={currentUser}
                  register={register}
                  name="paidBy"
                  error={errors.paidBy?.message}
                />
              </div>
              
              {/* 날짜 입력 필드 */}
              <div className="flex-1 relative">
                <label>
                  날짜
                </label>
                <input
                  type="date"
                  id="spentAt"
                  className="block w-full"
                  value={spentAt}
                  {...register('spentAt')}
                  onChange={handleSpentAtChange}
                />
              </div>
            </div>
            
            {/* 송금 받는 사람 (송금기록 선택 시) */}
            {expenseType === '송금기록' && (
              <MemberSelect
                id="receiverId"
                label="누구에게"
                members={members ?? []}
                currentUser={currentUser}
                register={register}
                name="receiverId"
                error={errors.receiverId?.message}
                excludeId={paidBy}
                placeholder="선택하세요"
              />
            )}
            
            {/* 참가자 (지출기록 선택 시) */}
            {expenseType === '지출기록' && (
              <ParticipantList
                selectedCurrency={currency}
                members={members ?? []}
                participants={selectedParticipants}
                currentUser={currentUser}
                splitType={splitType}
                amount={amount}
                multipliers={multipliers}
                manualAmounts={manualAmounts}
                formattedManualAmounts={formattedManualAmounts}
                calculateShare={calculateShare}
                toggleParticipant={toggleParticipant}
                adjustMultiplier={adjustMultiplier}
                handleManualAmountInputChange={handleManualAmountInputChange}
                handleManualAmountBlur={handleManualAmountBlur}
                onSplitTypeChange={handleSplitTypeChange}
              />
            )}
            
            <div className='h-16'/>
            {/* 삭제 및 수정 버튼 */}
            <div className="fixed bottom-0 left-0 right-0 max-w-screen-md p-4 mx-auto w-full bg-white flex justify-between gap-2 font-semibold">
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteLoading || submitLoading}
                className="text-lg flex-1 flex items-center justify-center py-3.5 px-4 bg-red-100 text-red-500 rounded-xl"
              >
                삭제하기
              </button>
              
              <button
                type="submit"
                disabled={submitLoading || deleteLoading}
                className="text-lg flex-3 flex items-center justify-center py-3.5 px-4 bg-blue-500 text-white rounded-xl"
              >
                {submitLoading ? (
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></span>
                ) : (
                  <></>
                )}
                수정하기
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}